// src/hooks/useElcCapByTechnology.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const ELC_CAP = gql`
  query ElcCapByTech($scenario: String!, $from: Int!, $to: Int!) {
    timeSeries(view: ELC_CAP, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group   # e.g. "ELC FROM WIND-ONSH", "ELC FROM GAS RR"
      value   # GW
    }
  }
`;

// --- Lookup table (Raw -> Plot) --------------------------------------------
const RAW_TO_PLOT = [
  // Order matters: put CCS/retrofit before base fuels
  [/^ELC\s+FROM\s+BIO\s+CCSRET$/, "BECCS"],
  [/^ELC\s+FROM\s+BIO\s+CCS$/,    "BECCS"],
  [/^ELC\s+FROM\s+BIO$/,          "Biomass"],

  [/^ELC\s+FROM\s+GAS\s+CCSRET$/, "Gas"],
  [/^ELC\s+FROM\s+GAS\s+CCS$/,    "Gas"],
  [/^ELC\s+FROM\s+MANFUELS$/,     "Gas"],
  [/^ELC\s+FROM\s+GAS\s+RR$/,     "Gas"],
  [/^ELC\s+FROM\s+GAS$/,          "Gas"],

  [/^ELC\s+FROM\s+COAL-COF$/,     "Coal"],
  [/^ELC\s+FROM\s+OIL$/,          "Oil"],

  [/^ELC\s+FROM\s+NUCLEAR$/,      "Nuclear"],

  [/^ELC\s+FROM\s+SOL[-\s]*PV$/,  "Solar PV"],

  [/^ELC\s+FROM\s+WIND[-\s]*OFFSH$/, "Offshore Wind"],
  [/^ELC\s+FROM\s+WIND[-\s]*ONSH$/,  "Onshore Wind"],
  // Fallbacks if dataset uses "WIND OFF"/"WIND ON"
  [/^ELC\s+FROM\s+WIND\s+OFF$/,   "Offshore Wind"],
  [/^ELC\s+FROM\s+WIND\s+ON$/,    "Onshore Wind"],

  [/^ELC\s+FROM\s+TIDAL$/,        "Tidal"],
  [/^ELC\s+FROM\s+WAVE$/,         "Wave"],
  [/^ELC\s+FROM\s+HYDRO$/,        "Hydropower"],
  [/^ELC\s+FROM\s+GEO$/,          "Geothermal"],
  [/^ELC\s+FROM\s+HYDROGEN$/,     "Hydrogen"],
  [/^ELC\s+FROM\s+IMPORTS$/,      "Interconnector"],

  [/^ELC\s+TO\s+EXPORTS$/,        "IGNORE"], // explicitly ignore
];

// Normalize: collapse weird hyphens/underscores, uppercase, single spaces
function norm(raw = "") {
  return String(raw)
    .toUpperCase()
    .replace(/[‐-–—]/g, "-")   // fancy dashes → hyphen
    .replace(/_/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-\s+/g, "-")
    .trim();
}

function toPlotName(groupRaw = "") {
  const g = norm(groupRaw)
    // unify variants like "ELC FROM WIND OFF" → "ELC FROM WIND OFF"
    .replace(/WIND-?OFFSH?/g, "WIND OFFSH")
    .replace(/WIND-?ONSH?/g,  "WIND ONSH")
    .replace(/SOL-?PV/g,      "SOL PV")
    .replace(/GAS-?RR/g,      "GAS RR")
    .replace(/BIO-?CCSRET/g,  "BIO CCSRET")
    .replace(/BIO-?CCS/g,     "BIO CCS");

  for (const [re, plot] of RAW_TO_PLOT) {
    if (re.test(g)) return plot;
  }
  // If it was an export but formatting didn’t match exactly, guard again:
  if (/^ELC\s+TO\s+EXPORTS$/.test(g)) return "IGNORE";
  return "Other";
}

export function useElcCapByTechnology({
  scenario,
  from = 2025,
  to = 2050,
  gwScale = 1,            // ELC_CAP already in GW
  includeThermal = true,  // show Gas/Coal/Oil/CHP etc. default true with this mapping
  minAbs = 1e-6,
}) {
  const { data, loading, error } = useQuery(ELC_CAP, {
    variables: { scenario, from, to },
    fetchPolicy: "cache-first",
  });

  const { labels, byTechYear, techList } = useMemo(() => {
    const rows = data?.timeSeries ?? [];
    const years = new Set();
    const byTechYear = {};

    // Build allowed set
    const ALL_TECHS = new Set([
      "Offshore Wind", "Onshore Wind", "Interconnector", "Solar PV",  "Geothermal", "Hydrogen", "Biomass", "BECCS", "Nuclear",
      "Gas", "Gas with CCS", "CHP",
    ]);
    const RENEW_PLUS = new Set([
      "Offshore Wind", "Onshore Wind", "Solar PV", "Wave",
      "Hydropower", "Geothermal", "Hydrogen", "Biomass", "BECCS",
      "Interconnector", "Nuclear"
    ]);
    const allowed = includeThermal ? ALL_TECHS : RENEW_PLUS;

    for (const r of rows) {
      const y = Number(r.year);
      let v = Number(r.value) || 0;

      if (!Number.isFinite(y)) continue;
      if (Math.abs(v) < minAbs) v = 0;

      const tech = toPlotName(r.group || "");
      if (tech === "IGNORE") continue;
      if (!allowed.has(tech)) continue;

      years.add(y);
      byTechYear[tech] ||= {};
      byTechYear[tech][y] = (byTechYear[tech][y] ?? 0) + v * gwScale;
    }

    const labels = Array.from(years).sort((a, b) => a - b);

    // Stable order for display
    const ORDER = [
      "Offshore Wind", "Onshore Wind", "Solar PV", "Tidal", "Wave",
      "Hydropower", "Geothermal", "Hydrogen", "Biomass", "BECCS",
      "Interconnector", "Nuclear",
      "Gas", "Gas with CCS", "Coal", "Oil", "CHP", "Other",
    ];
    const techList = ORDER.filter((k) => byTechYear[k] && Object.keys(byTechYear[k]).length);

    return { labels, byTechYear, techList };
  }, [data, gwScale, includeThermal, minAbs]);

  return { loading, error, labels, byTechYear, techList };
}
